import { useEffect, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { Shell } from './Shell'
import { Sidebar, type WorkspaceChip } from './Sidebar'
import { TopBar } from './TopBar'
import { isInsideClient } from './rail.ts'
import { crumbsFor, flattenRoutes, matchRoute, resolveNavGroups } from '../../nav/manifest.ts'
import { useOptionalFleetApi } from '../../api/context.ts'

export interface ConsoleChromeProps {
  children: ReactNode
  /** The signed-in account, in the authenticated tree. Absent renders the static "Nth AI" chip. */
  workspace?: WorkspaceChip
  /** `<AccountMenu/>`, in the authenticated tree. Absent renders no account control at all. */
  accountSlot?: ReactNode
  /**
   * The signed-in principal, for the settings panel. Separate from `workspace` on purpose:
   * `workspace` falls back to a static "Nth AI" chip so the rail always has a foot, and
   * reusing it here would report a signed-in user on the demo path where nobody signed in.
   */
  account?: { name?: string; username?: string }
}

/**
 * Binds the navigation manifest to the chrome and filters it from the current database-managed
 * capability set. The server independently enforces the same grants; hiding a rail item is only
 * presentation, never the security boundary.
 */
export function ConsoleChrome({ children, workspace, accountSlot, account }: ConsoleChromeProps) {
  const { pathname } = useLocation()
  const api = useOptionalFleetApi()
  const [capabilities, setCapabilities] = useState<string[] | undefined>(undefined)

  useEffect(() => {
    let alive = true
    if (!api) return
    setCapabilities([])
    Promise.resolve(api.currentAccess()).then(
      (access) => {
        if (!alive) return
        setCapabilities(
          access.permissions.includes('*') ? flattenRoutes().map((route) => route.perm) : access.permissions,
        )
      },
      () => {
        if (alive) setCapabilities([])
      },
    )
    return () => {
      alive = false
    }
  }, [api])

  const groups = resolveNavGroups({ capabilities })
  const hit = matchRoute(pathname)

  // The rail highlights the top-level route the current path sits under, so a child tab
  // does not un-highlight its own section.
  const activeHref = hit
    ? groups
        .flatMap((g) => g.items)
        .map((i) => i.href)
        .filter((href) => href === '/' ? pathname === '/' : pathname.startsWith(href))
        .sort((a, b) => b.length - a.length)[0] ?? pathname
    : pathname

  // Inside ONE client (`/clients/:clientId/...`), the client's own tabs are the navigation that
  // matters, so the rail narrows to icons and the client's screens take the width (Mark, 5 October).
  // The clients LIST is not inside a client and keeps the full rail.
  const rail = isInsideClient(pathname)

  return (
    <Shell
      rail={rail}
      sidebar={
        <Sidebar
          collapsed={rail}
          groups={groups}
          activeHref={activeHref}
          workspace={workspace ?? { initials: 'NA', name: 'Nth AI', sub: 'Fleet operator' }}
        />
      }
      topbar={<TopBar crumbs={crumbsFor(pathname)} accountSlot={accountSlot} account={account} />}
    >
      {children}
    </Shell>
  )
}
